import { ResourceCategory } from '../../domain/entities/category';
import { MediaImage } from '../../domain/entities/media';
import {
  RESOURCE_TYPES,
  ResourceType,
  TourismResource,
} from '../../domain/entities/tourism-resource';

import { toResourceCategory } from './category.mapper';
import { toEventSchedule } from './event-schedule.mapper';
import { toGeoPoint, toMediaImage } from './media.mapper';
import {
  RawJson,
  asRecord,
  readArray,
  readId,
  readLocalized,
  readString,
  readUpdatedAt,
} from './raw-json';

function toResourceType(value: string | null): ResourceType | null {
  if (value === null) {
    return null;
  }
  const normalized = value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  return RESOURCE_TYPES.find((type) => normalized.startsWith(type)) ?? null;
}

function toCategories(source: RawJson): readonly ResourceCategory[] {
  return readArray(source, 'resourceCategories', 'categories', 'categorias')
    .map((raw, index) => toResourceCategory(raw, index))
    .filter((category): category is ResourceCategory => category !== null);
}

function toImages(source: RawJson): readonly MediaImage[] {
  const cover = source['image'] ?? source['thumbnail'];
  const gallery = readArray(source, 'multimedia', 'images', 'imagenes', 'media', 'gallery');

  return [cover, ...gallery]
    .map(toMediaImage)
    .filter((image): image is MediaImage => image !== null);
}

export function toTourismResource(raw: unknown): TourismResource | null {
  const record = asRecord(raw);
  const id = readId(record);
  const title = readLocalized(record, 'title', 'name', 'label');

  if (id === null || title === null) {
    return null;
  }

  const type = toResourceType(readString(record, 'resourceType', 'type', 'tipo', 'bundle'));

  return {
    id,
    type,
    title,
    summary: readLocalized(record, 'shortDescription', 'summary', 'resumen', 'teaser', 'excerpt'),
    description: readLocalized(
      record,
      'htmlContent',
      'htmlDescription',
      'description',
      'descripcion',
      'body',
      'content',
    ),
    categories: toCategories(record),
    images: toImages(record),
    location: toGeoPoint(record),
    populationId: readString(record, 'populationId', 'population_id', 'poblacion'),
    schedule: type === 'evento' ? toEventSchedule(record) : null,
    updatedAt: readUpdatedAt(record),
  };
}
